import * as _ from 'lodash';

import * as API from '../api';

import type { ProjectsV2Store } from './types';

/**
 * Wrapper around our get and put helpers, but with better
 * defaults for projectsV2-related stuff.
 * Specifically, it always includes dedupe and encoding params.
 */
export const get = <R>(endpoint: string, params: Record<string, unknown> = {}) =>
  API.get<R>(endpoint, true, params, { encodeQuery: true });

//  Helper for determining if ALL projects across ALL pages are selected.
export const areAllProjectsSelected = (selectedProjects: ProjectsV2Store['selectedProjects']): selectedProjects is 'all' =>
  selectedProjects === 'all';

//  Checks if any project on the current page are selected (including "all").
export const isAnyProjectOnPageSelected = (projects: ProjectsV2Store['projects'], selectedProjects: ProjectsV2Store['selectedProjects']) =>
  areAllProjectsSelected(selectedProjects) ||
  (projects.length > 0 &&
    selectedProjects.length > 0 &&
    _.intersection(projects.map(({ id }) => id), selectedProjects.map(({ id }) => id)).length > 0);

//  Checks that every project on the current page is selected (including "all").
export const isEveryProjectOnPageSelected = (projects: ProjectsV2Store['projects'], selectedProjects: ProjectsV2Store['selectedProjects']) =>
  areAllProjectsSelected(selectedProjects) ||
  (projects.length > 0 && (_.difference(projects.map(({ id }) => id),
    selectedProjects.map(({ id }) => id)).length === 0));

export const areAllProjectsIndividuallySelected = (draftState: ProjectsV2Store) => {
  const categoriesSummary = draftState.categoriesSummary;
  //  totalIssuesCount will be undefined if statusSummary isn't fetched yet, which shouldn't happen, but we check for safety.
  return !!categoriesSummary &&
    !areAllProjectsSelected(draftState.selectedProjects) &&
    draftState.selectedProjects.length === categoriesSummary[draftState.category];
};

export const removeEmptyAttributesFromObject = <T extends object>(object: T) => {
  return Object.entries(object).reduce((acum, [key, value]) => {
    if (!_.isNil(value) && value !== '') {
      return { ...acum, [key]: value };
    }
    return acum;
  }, {} as T);
};
